<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <vue-element-loading :active="busy"></vue-element-loading>
        <card-page :card-title="cardTitle" :switch-models="switchModels">
          <vue-form-generator
            ref="form"
            :model="model"
            :options="formOptions"
            :schema="schema"
          />
        </card-page>
        <card-page card-title="Placing Brokers">
          <additive-widget
            ref="formPlacingBrokers"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaPlacingBrokers"
            label="Placing Brokers"
          />
        </card-page>
        <card-page card-title="Cover Holders">
          <additive-widget
            ref="formCoverHolders"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaCoverHolders"
            label="Cover Holders"
          />
        </card-page>
        <card-page card-title="Facility Managers">
          <additive-widget
            ref="formFacilityManagers"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaFacilityManagers"
            label="Facility Managers"
          />
        </card-page>
        <card-page card-title="Intermediary">
          <additive-widget
            ref="formIntermediary"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaIntermediary"
            label="Intermediary"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import FormModelMixin from '@/components/mixins/FormModelMixin';
import {selectOptions, datepickerOptions, arrayOptions} from '@/components/form/FormElementOption';
import AdditiveWidget from '@/components/form/fields/AdditiveWidget';
import FormFieldValidationMixin from '@/components/mixins/FormFieldValidationMixin';
import moment from 'moment';
import CardPage from '../../components/Views/CardPage';

export default {
  name: 'FacilityForm',
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    AdditiveWidget,
    CardPage
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      selectedReferences: [],
      formReferences: ['form'],
      additiveForms: [
        'formPlacingBrokers',
        'formCoverHolders',
        'formFacilityManagers',
        'formIntermediary'
      ],
      model: {
        FacilityReference: '',
        UniqueMarketReference: '',

        ClassOfBusiness_FK: null,
        FacilityType_FK: null,
        CompanyLeadInsurer_FK: null,
        TerritoryRiskLocation_FK: null,
        TerritoryInsuredDomicile_FK: null,
        TerritoryTerritorialLimit_FK: null,

        InceptionDate: null,
        ExpiryDate: null,
        Signing_PC: null,
        CapacityLimit: null,
        Notifiable_PC: null,

        CompanyLossAdjuster_FK: null,
        MaximumBackDateDay: null,
        YearOfAccount: null,
        PeriodOfInsuranceMonth: null,
        MaximumPeriodMonth: null,
        MaximumAdvanceDay: null,
        NoticePeriodDay: null,
        ProductionReportingPeriodDay: null,
        PaidPremiumReportingPeriodDay: null,
        ClaimsReportingPeriodDay: null,
        RemittanceDay: null,
        MaximumFacilityDiscount_PC: null,
        ContingentCommission_PC: null,
        ProfitCommission_PC: null,
        ProfitCommissionDate: null,
        BARefDate: null,
        BARefNo: null,

        IsEnabled: null,

        placing_brokers: [],
        managers: [],
        intermediary: [],
        coverholders: []
      },
      schema: {
        groups: [
          {
            styleClasses: 'w-full mt-0',
            legend: 'General',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Reference',
                model: 'FacilityReference',
                required: () => !this.readonly,
                validator: ['required', 'string']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Unique Market Reference',
                model: 'UniqueMarketReference',
                required: () => !this.readonly,
                validator: ['required', 'string']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Year of Account',
                model: 'YearOfAccount',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Class of Business',
                model: 'ClassOfBusiness_FK',
                values: () => this.classOfBusiness,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Facility Type',
                model: 'FacilityType_FK',
                values: () => this.facilityType,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Capacity Limit',
                model: 'CapacityLimit',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Risk Location',
                model: 'TerritoryRiskLocation_FK',
                values: () => this.territory,
                selectOptions,
                required: () => !this.readonly,
                validator: ['required']
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Insured Domicile',
                model: 'TerritoryInsuredDomicile_FK',
                values: () => this.territory,
                selectOptions,
                required: () => !this.readonly,
                validator: ['required']
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Territorial Limit',
                model: 'TerritoryTerritorialLimit_FK',
                values: () => this.territory,
                selectOptions,
                required: () => !this.readonly,
                validator: ['required']
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Company Lead Insurer',
                model: 'CompanyLeadInsurer_FK',
                values: () => this.companyLeadInsurer,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Company Loss Adjuster',
                model: 'CompanyLossAdjuster_FK',
                values: () => this.companyLossAdjuster,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                disabled: () => this.readonly,
                type: 'vsCheckbox',
                styleClasses: 'vx-col w-1/3',
                label: 'Use company lead logo',
                model: 'UseCompanyLeadLogo'
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Dates',
            fields: [
              {
                ...datepickerOptions,
                disabled: () => this.readonly,
                styleClasses: 'vx-col w-1/3',
                label: 'Inception Date',
                model: 'InceptionDate',
                required: () => !this.readonly,
                onChanged: () => {
                  this.validateField('ExpiryDate');
                },
                validator: [
                  'required',
                  (value, field, model) => {
                    if (model.ExpiryDate) {
                      let expiryDate = model.ExpiryDate;
                      let inceptionDate = moment(model.InceptionDate).unix();

                      if (typeof expiryDate === 'string') {
                        expiryDate = moment(
                          model.ExpiryDate,
                          'YYYY-MM-DD'
                        ).unix();
                      } else {
                        expiryDate = expiryDate / 1000;
                      }

                      if (inceptionDate > expiryDate) {
                        return 'Inception Date must be before Expiry Date.';
                      }
                    }

                    return true;
                  }
                ]
              },
              {
                disabled: () => this.readonly,
                ...datepickerOptions,
                styleClasses: 'vx-col w-1/3',
                label: 'Expiry Date',
                model: 'ExpiryDate',
                required: () => !this.readonly,
                onChanged: () => {
                  this.validateField('InceptionDate');
                },
                validator: [
                  'required',
                  'string',
                  (value, field, model) => {
                    if (model.InceptionDate) {
                      let inceptionDate = model.InceptionDate;
                      let expiryDate = moment(model.ExpiryDate).unix();

                      if (typeof inceptionDate === 'string') {
                        inceptionDate = moment(
                          model.InceptionDate,
                          'YYYY-MM-DD'
                        ).unix();
                      } else {
                        inceptionDate = inceptionDate / 1000;
                      }

                      if (expiryDate < inceptionDate) {
                        return 'Expiry Date must be after Inception Date.';
                      }
                    }

                    return true;
                  }
                ]
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                ...datepickerOptions,
                styleClasses: 'vx-col w-1/3',
                label: 'BA Ref Date',
                model: 'BARefDate',
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'BA Ref #',
                model: 'BARefNo',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              }
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Figures',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Signing %',
                model: 'Signing_PC',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Notifiable %',
                model: 'Notifiable_PC',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                step: '0.01',
                styleClasses: 'vx-col w-1/3',
                label: 'Maximum Facility Discount %',
                model: 'MaximumFacilityDiscount_PC',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                step: '0.01',
                styleClasses: 'vx-col w-1/3',
                label: 'Contingent Commission %',
                model: 'ContingentCommission_PC',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                step: 0.01,
                styleClasses: 'vx-col w-1/3',
                label: 'Profit Commission',
                model: 'ProfitCommission_PC'
              },
              {
                disabled: () => this.readonly,
                ...datepickerOptions,
                styleClasses: 'vx-col w-1/3',
                label: 'Profit Commission Date',
                model: 'ProfitCommissionDate'
              }
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Limits',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Period of Insurance (Months)',
                model: 'PeriodOfInsuranceMonth',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Maximum Period (Months)',
                model: 'MaximumPeriodMonth',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Maximum Back Date (Days)',
                model: 'MaximumBackDateDay',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Maximum Advance Date (Days)',
                model: 'MaximumAdvanceDay',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Notice Period (Days)',
                model: 'NoticePeriodDay',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Remittance (Days)',
                model: 'RemittanceDay',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Production Reporting Period (Days)',
                model: 'ProductionReportingPeriodDay',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Paid Premium Reporting (Days)',
                model: 'PaidPremiumReportingPeriodDay',
                required: () => !this.readonly,
                min: 0,
                validator: ['required', 'positive', 'numeric']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                min: 0,
                styleClasses: 'vx-col w-1/3',
                label: 'Claims Reporting Period (Days)',
                model: 'ClaimsReportingPeriodDay',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric']
              }
            ]
          }
        ]
      },
      schemaPlacingBrokers: {
        groups: [
          {
            styleClasses: 'w-full pt-3',
            fields: [
              {
                model: 'placing_brokers',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'vx-col w-1/4',
                            label: 'Placing Broker',
                            model: 'CompanyPlacingBroker_FK',
                            values: () => this.placingBrokers,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Inception Date',
                            model: 'InceptionDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'ExpiryDate',
                                'placing_brokers'
                              );
                            },
                            validator: [
                              'required',
                              'string',
                              (value, field, model) => {
                                if (model.ExpiryDate) {
                                  let expiryDate = model.ExpiryDate;
                                  let inceptionDate = moment(
                                    model.InceptionDate
                                  ).unix();

                                  if (typeof expiryDate === 'string') {
                                    expiryDate = moment(
                                      model.ExpiryDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    expiryDate = expiryDate / 1000;
                                  }

                                  if (inceptionDate > expiryDate) {
                                    return 'Inception Date must be before Expiry Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Expiry Date',
                            model: 'ExpiryDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'InceptionDate',
                                'placing_brokers'
                              );
                            },
                            validator: [
                              'required',
                              'string',
                              (value, field, model) => {
                                if (model.InceptionDate) {
                                  let inceptionDate = model.InceptionDate;
                                  let expiryDate = moment(
                                    model.ExpiryDate
                                  ).unix();

                                  if (typeof inceptionDate === 'string') {
                                    inceptionDate = moment(
                                      model.InceptionDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    inceptionDate = inceptionDate / 1000;
                                  }

                                  if (expiryDate < inceptionDate) {
                                    return 'Expiry Date must be after Inception Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      schemaCoverHolders: {
        groups: [
          {
            styleClasses: 'w-full pt-3',
            fields: [
              {
                model: 'coverholders',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'vx-col w-1/4',
                            label: 'Coverholders',
                            model: 'CompanyCoverholder_FK',
                            values: () => this.coverholders,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Inception Date',
                            model: 'InceptionDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'ExpiryDate',
                                'coverholders'
                              );
                            },
                            validator: [
                              'required',
                              (value, field, model) => {
                                if (model.ExpiryDate) {
                                  let expiryDate = model.ExpiryDate;
                                  let inceptionDate = moment(
                                    model.InceptionDate
                                  ).unix();

                                  if (typeof expiryDate === 'string') {
                                    expiryDate = moment(
                                      model.ExpiryDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    expiryDate = expiryDate / 1000;
                                  }

                                  if (inceptionDate > expiryDate) {
                                    return 'Inception Date must be before Expiry Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Expiry Date',
                            model: 'ExpiryDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'InceptionDate',
                                'coverholders'
                              );
                            },
                            validator: [
                              'required',
                              (value, field, model) => {
                                if (model.InceptionDate) {
                                  let inceptionDate = model.InceptionDate;
                                  let expiryDate = moment(
                                    model.ExpiryDate
                                  ).unix();

                                  if (typeof inceptionDate === 'string') {
                                    inceptionDate = moment(
                                      model.InceptionDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    inceptionDate = inceptionDate / 1000;
                                  }

                                  if (expiryDate < inceptionDate) {
                                    return 'Expiry Date must be after Inception Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      schemaFacilityManagers: {
        groups: [
          {
            styleClasses: 'w-full pt-3',
            fields: [
              {
                model: 'managers',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'vx-col w-1/4',
                            label: 'Facility Manager',
                            model: 'CompanyFacilityManager_FK',
                            values: () => this.coverholders,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Inception Date',
                            model: 'InceptionDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'ExpiryDate',
                                'managers'
                              );
                            },
                            validator: [
                              'string',
                              (value, field, model) => {
                                if (model.ExpiryDate) {
                                  let expiryDate = model.ExpiryDate;
                                  let inceptionDate = moment(
                                    model.InceptionDate
                                  ).unix();

                                  if (typeof expiryDate === 'string') {
                                    expiryDate = moment(
                                      model.ExpiryDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    expiryDate = expiryDate / 1000;
                                  }

                                  if (inceptionDate > expiryDate) {
                                    return 'Inception Date must be before Expiry Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Expiry Date',
                            model: 'ExpiryDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'InceptionDate',
                                'managers'
                              );
                            },
                            validator: [
                              'string',
                              (value, field, model) => {
                                if (model.InceptionDate) {
                                  let inceptionDate = model.InceptionDate;
                                  let expiryDate = moment(
                                    model.ExpiryDate
                                  ).unix();

                                  if (typeof inceptionDate === 'string') {
                                    inceptionDate = moment(
                                      model.InceptionDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    inceptionDate = inceptionDate / 1000;
                                  }

                                  if (expiryDate < inceptionDate) {
                                    return 'Expiry Date must be after Inception Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      schemaIntermediary: {
        groups: [
          {
            styleClasses: 'w-full pt-3',
            fields: [
              {
                model: 'intermediary',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'vx-col w-1/4',
                            label: 'Intermediary',
                            model: 'CompanyIntermediary_FK',
                            values: () => this.intermediaries,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Inception Date',
                            model: 'InceptionDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'ExpiryDate',
                                'intermediary'
                              );
                            },
                            validator: [
                              'string',
                              (value, field, model) => {
                                if (model.ExpiryDate) {
                                  let expiryDate = model.ExpiryDate;
                                  let inceptionDate = moment(
                                    model.InceptionDate
                                  ).unix();

                                  if (typeof expiryDate === 'string') {
                                    expiryDate = moment(
                                      model.ExpiryDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    expiryDate = expiryDate / 1000;
                                  }

                                  if (inceptionDate > expiryDate) {
                                    return 'Inception Date must be before Expiry Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          },
                          {
                            ...datepickerOptions,
                            disabled: () => this.readonly,
                            styleClasses: 'vx-col w-1/4',
                            label: 'Expiry Date',
                            model: 'ExpiryDate',
                            required: () => !this.readonly,
                            onChanged: () => {
                              this.validateGroupedField(
                                'InceptionDate',
                                'intermediary'
                              );
                            },
                            validator: [
                              'string',
                              (value, field, model) => {
                                if (model.InceptionDate) {
                                  let inceptionDate = model.InceptionDate;
                                  let expiryDate = moment(
                                    model.ExpiryDate
                                  ).unix();

                                  if (typeof inceptionDate === 'string') {
                                    inceptionDate = moment(
                                      model.InceptionDate,
                                      'YYYY-MM-DD'
                                    ).unix();
                                  } else {
                                    inceptionDate = inceptionDate / 1000;
                                  }

                                  if (expiryDate < inceptionDate) {
                                    return 'Expiry Date must be after Inception Date.';
                                  }
                                }

                                return true;
                              }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true
      },
      classOfBusiness: [],
      facilityType: [],
      companyLeadInsurer: [],
      territory: [],
      companyLossAdjuster: [],
      coverholders: [],
      intermediaries: [],
      placingBrokers: [],
      options: [
        {
          endpoint: () => this.$api.facilityClassOfBusiness.list(),
          optionsFieldName: 'classOfBusiness',
          nameFieldName: 'FullName',
          idFieldName: 'ClassOfBusiness_FK',
        },
        {
          endpoint: () => this.$api.facilityType.list(),
          optionsFieldName: 'facilityType',
          nameFieldName: 'Type',
          idFieldName: 'FacilityType_FK'
        },
        {
          endpoint: () => this.$api.companies.insurers(),
          optionsFieldName: 'companyLeadInsurer',
          nameFieldName: 'CompanyName',
          idFieldName: 'CompanyLeadInsurer_FK'
        },
        {
          endpoint: () => this.$api.companies.lossAdjusters(),
          optionsFieldName: 'companyLossAdjuster',
          nameFieldName: 'CompanyName',
          idFieldName: 'CompanyLossAdjuster_FK'
        },
        {
          endpoint: () => this.$api.facilityTerritory.list(),
          optionsFieldName: 'territory',
          nameFieldName: 'Name',
          idFieldName: 'TerritoryRiskLocation_FK'
        },
        {
          optionsFieldName: 'territory',
          idFieldName: 'TerritoryInsuredDomicile_FK'
        },
        {
          optionsFieldName: 'territory',
          idFieldName: 'TerritoryTerritorialLimit_FK'
        },
        {
          endpoint: () => this.$api.companies.placingBrokers(),
          optionsFieldName: 'placingBrokers',
          nameFieldName: 'CompanyName',
          idFieldName: 'CompanyPlacingBroker_FK',
          modelArrayName: 'placing_brokers'
        },
        {
          endpoint: () => this.$api.companies.coverholders(),
          optionsFieldName: 'coverholders',
          nameFieldName: 'CompanyName',
          idFieldName: 'CompanyCoverholder_FK',
          modelArrayName: 'coverholders'
        },
        {
          endpoint: () => this.$api.companies.coverholders(),
          optionsFieldName: 'coverholders',
          nameFieldName: 'CompanyName',
          idFieldName: 'CompanyFacilityManager_FK',
          modelArrayName: 'managers'
        },
        {
          endpoint: () => this.$api.companies.all({params:{IsEnabled: 1}}),
          optionsFieldName: 'intermediaries',
          nameFieldName: 'CompanyName',
          idFieldName: 'CompanyIntermediary_FK',
          modelArrayName: 'intermediary'
        }
      ]
    };
  },
  computed: {
    switchModels() {
      if (!this.model) {
        return [];
      }

      return [
        {
          model: this.model.IsPublished,
          activeLabel: 'Published',
          inactiveLabel: 'Unpublished',
        },
        {
          model: this.model.IsEnabled,
          activeLabel: 'Active',
          inactiveLabel: 'Inactive',
        }
      ];
    }
  }
};
</script>
